<template>
    <div v-if="doctor !== null && scheduleTimezone !== null">
        <div
            v-if="$store.state.configs.timezone == 1"
            class="row justify-content-between">
            <div class="col-9 col-sm-6">
                <div class="mb-3">
                    <label for="timezone">スケジュールのタイムゾーン</label>
                    <div class="form-group mt-0 mb-1">
                        <div class="form-check form-check-inline">
                            <input
                                v-model="isDeviceScheduleTimezone"
                                type="checkbox"
                                id="is_device_timezone"
                                class="form-check-input"
                                @change="syncDeviceTimezone">
                            <label
                                for="is_device_timezone"
                                class="mb-0">
                                デバイスのタイムゾーンに合わせる
                            </label>
                        </div>
                    </div>
                    <select
                        v-model="scheduleTimezone"
                        :disabled="isDeviceScheduleTimezone == 1"
                        @change="openDoctorTimezoneConfirmModal"
                        :class="{ 'is-invalid' : doctor.timezone !== scheduleTimezone }"
                        class="form-control">
                        <option
                            v-for="timezone in timezones"
                            :value="timezone.key">
                            {{ timezone.value }}
                        </option>
                    </select>
                    <div
                        v-if="doctor.timezone !== scheduleTimezone"
                        class="cautionTimezone text-danger">
                        <b-icon-exclamation-triangle-fill/>プロフィールと異なるタイムゾーンが設定されています
                    </div>
                </div>
            </div>
            <div v-if="!doctor.doctor_metadata.is_google_dev" class="col-5 col-sm-3 d-flex">
                <button 
                    class="btn btn-yokumiru-orange calendar-btn"
                    @click="openDoctorCalendarExportModal">
                    カレンダー連携
                </button>
            </div>
            <div v-else class="col-5 col-sm-3 d-flex align-items-end">
                <div v-if="doctor.google_social_account === null" class="w-100">
                    <b-button 
                        class="btn btn-yokumiru-blue link-calendar-btn" 
                        @click="openDoctorGoogleCalendarLinkageModal">
                        Googleカレンダーと連携
                    </b-button>
                </div>
                <div v-else class="w-100">
                    <b-button 
                        class="btn btn-yokumiru-blue link-calendar-btn" 
                        @click="openDoctorGoogleAccountLinkModal">
                        Googleカレンダーと連携中
                    </b-button>
                </div>
            </div>
            
        </div>

        <div class="vc-basic-schedule-table card">
            <div class="card-header">基本スケジュール</div>
            <div class="card-body">
                <div
                    v-if="$store.state.configs.reservation_multiple"
                    class="d-flex align-items-center">
                    <span class="mr-2">
                        {{ intervalWithUnit }}毎に予約できる件数
                    </span>
                    <select
                        v-model="patientsPerInterval"
                        @change="updatePatientsPerInterval"
                        style="width: 64px;"
                        class="custom-select custom-select-sm">
                        <option
                            v-for="n of 10"
                            :value="n"
                            name="patients_per_interval">
                            {{ `${n}件` }}
                        </option>
                    </select>
                </div>
                <FullCalendar
                    id="calendar"
                    ref="fullCalendar"
                    class="calendar-wrapper mt-3 mb-3"
                    :class="{ 'schedule-calendar-consul' : $locale.type === 'consul' } "
                    :plugins="calendarPlugins"
                    :default-view="config.fullCalendar.defaultView"
                    :views="config.fullCalendar.views"
                    :header="config.fullCalendar.header"
                    :height="config.fullCalendar.height"
                    :locale="config.fullCalendar.locale"
                    :button-text="config.fullCalendar.buttonText"
                    :events="fullCalendarEvents"
                    :event-text-color="config.fullCalendar.eventTextColor"
                    :all-day-slot="config.fullCalendar.allDaySlot"
                    :display-event-end="config.fullCalendar.displayEventEnd"
                    :event-time-format="config.fullCalendar.eventTimeFormat"
                    :column-header-format="config.fullCalendar.columnHeaderFormat"
                    @dateClick="dateClick"
                    :event-render="eventRender"
                    :dates-render="datesRender">
                </FullCalendar>
            </div>
        </div>

        <b-modal
            v-model="modalShow"
            :title="`${days[ currentDay ]}曜日の基本スケジュール登録`"
            size="xl"
            id="modalSchedule"
            ok-title="登録"
            ok-variant="yokumiru-orange"
            ok-only
            @ok="handleOk">
            <form @submit.stop.prevent="postSchedule">
                <div class="small">
                    <ul class="pl-3">
                        <li>チェックなしの時間帯は「休診」&nbsp;チェックありの時間帯は「{{ $locale.consultation_host }}」となります。</li>
                    </ul>
                </div>

                <div class="d-flex justify-content-center">
                    <div
                        v-for="(col, colIdx) in schedule.checkboxes[intervalIdx]"
                        class="ml-3 mr-3">
                        <div>
                            <input
                                type="checkbox"
                                class="check-col-all"
                                :id="`col${colIdx}`"
                                :value="colIdx"
                                :checked="existsCheckedIdx( colIdx )"
                                @change="toggleColumnAll( col, colIdx, $event )">
                            <label :for="`col${colIdx}`">
                                {{ `${timeForHuman(col[0].start_time.slice(0, -3))} - ${timeForHuman(col[ col.length - 1 ].end_time.slice(0, -3), false)}` }}
                            </label>
                        </div>

                        <hr class="mt-0 mb-2">

                        <template v-for="(row, rowIdx) in col">
                            <div>
                                <input
                                    v-model="schedule.selectedData"
                                    type="checkbox"
                                    :id="`${row.start_time}-${rowIdx}`"
                                    :value="row"
                                    @change="changeColumnState( row )">
                                <label :for="`${row.start_time}-${rowIdx}`">
                                    {{ `${timeForHuman(row.start_time.slice(0, -3))} - ${timeForHuman(row.end_time.slice(0, -3), false)}` }}
                                </label>
                            </div>
                        </template>
                    </div>
                </div>
            </form>
        </b-modal>

        <doctor-timezone-confirm-modal/>
        <doctor-timezone-dst-confirm-modal/>
        <doctor-calendar-export-modal/>
        <doctor-google-calendar-linkage-modal/>
        <doctor-google-account-link-cancel-modal/>
        <doctor-google-account-link-modal
            :doctor="doctor"
            @on-link-cancel="openDoctorGoogleAccountLinkCancelModal()">
        </doctor-google-account-link-modal>
    </div>
</template>

<script>
    import constants from '../../constants'
    import YokumiruHelper from '../../YokumiruHelper'
    import moment from 'moment-timezone'

    import FullCalendar from '@fullcalendar/vue'
    import dayGridPlugin from '@fullcalendar/daygrid'
    import timeGridPlugin from '@fullcalendar/timegrid'
    import interactionPlugin from '@fullcalendar/interaction'
    import ja from '@fullcalendar/core/locales/ja'
    import '@fullcalendar/core/main.css'
    import '@fullcalendar/daygrid/main.css'
    import '@fullcalendar/timegrid/main.css'

    export default {
        components: {
            FullCalendar,
        },

        props: {
            timezones: {
                type: Array,
                required: true,
            },
        },

        data: function() {
            return {
                helper: YokumiruHelper,
                calendarPlugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
                modalShow: false,
                inProgress: false,
                doctor: null,
                scheduleTimezone: null,
                isDeviceScheduleTimezone: false,
                basicSchedules: [],
                exceptionSchedules: [],
                patientsPerInterval: 1,
                schedule: {
                    checkboxes: [],
                    selectedData: [],
                    columnHours: constants.column_hours,
                },
                days: constants.days,
                currentDay: 0,
                checkedColumnAllList: [],
                errors: {},
                saved: false,
                failed: false,
                savedMessage: '基本スケジュールを保存しました。',
                config: {
                    fullCalendar: {
                        locale: ja,
                        header: {
                            left: '',
                            center: '',
                            right : 'dayGridWeek,timeGridWeek',
                        },
                        buttonText: {
                            dayGridWeek: '週',
                            timeGridWeek: '週（タイムライン）',
                        },
                        height: 150,
                        eventTextColor: '#fff',
                        defaultView: 'dayGridWeek',
                        views: {
                            timeGridWeek: {
                                slotLabelFormat: {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                },
                            },
                        },
                        eventTimeFormat: {
                            hour: '2-digit',
                            minute: '2-digit',
                        },
                        columnHeaderFormat: {
                            weekday: 'short',
                        },
                        displayEventEnd: true,
                        editable: false,
                        selectable: false,
                        allDaySlot: false,
                    },
                },
                fullCalendarEvents : [],
                daysOptions: [
                    {text: '日', value: 0},
                    {text: '月', value: 1},
                    {text: '火', value: 2},
                    {text: '水', value: 3},
                    {text: '木', value: 4},
                    {text: '金', value: 5},
                    {text: '土', value: 6},
                ],
            }
        },

        computed: {
            /**
             * 相談時間の間隔（分）
             *
             * @return {number}
             */
            interval: function () {
                return constants.minutes_intervals.find((interval) => interval === this.doctor.minutes_interval)
            },

            /**
             * 相談時間の間隔インデックス（0=15分, 1=30分, 2=60分）
             *
             * @return {number}
             */
            intervalIdx: function () {
                return constants.minutes_intervals.findIndex((interval) => interval === this.doctor.minutes_interval)
            },

            /**
             * 相談時間の間隔表記（分）
             *
             * @return {string}
             */
            intervalWithUnit: function () {
                return this.interval < 60 ? `${this.interval}分間` : `${this.interval / 60}時間`
            },

            /**
             * 設定中スケジュールのフィルタリング
             * 曜日が一致するデータのみを抽出する
             *
             * @return {array}
             */
            filteredSchedules: function () {
                return this.basicSchedules.filter((schedule) => schedule.day === this.currentDay)
            },
        },

        created: async function () {
            await this.fetchMe()
            await this.fetchBasicSchedules()
            await this.fetchExceptionSchedules()

            // 複数人予約の最大人数
            this.patientsPerInterval = this.doctor.patients_per_interval

            this.bindFullCalendar()
            this.syncDeviceTimezone()

            bus.$on('change-link-calendar', this.changeLinkCalendar)
            bus.$on('schedule-timezone-confirmed', this.refreshCalendar)
            bus.$on('schedule-timezone-canceled', this.initScheduleTimezoneAsCanceled)

            if (this.$route.query.redirect === 'google') {
                this.openDoctorGoogleAccountLinkModal()
            }
            else if (this.$route.query.redirect === 'exists') {
                bus.$emit('alert-danger', '既に連携済みのアカウントです。')
            }
        },

        beforeDestroy: function () {
            bus.$off('change-link-calendar')
            bus.$off('schedule-timezone-confirmed')
            bus.$off('schedule-timezone-canceled')
        },

        methods: {
            /**
             * ログイン医師取得
             */
            fetchMe: function () {
                return axios.get(constants.doctor.me.endpoint.base)
                    .then(({data}) => {
                        this.doctor = data.data
                        this.initScheduleTimezone()
                    })
            },

            /**
             * スケジュールのタイムゾーンをセット
             */
            initScheduleTimezone: function() {
                this.scheduleTimezone = this.doctor.doctor_metadata.schedule_timezone
                this.isDeviceScheduleTimezone = this.doctor.doctor_metadata.is_device_schedule_timezone
            },

            /**
             * スケジュールのタイムゾーンをセット（変更キャンセル経由時用）
             */
            initScheduleTimezoneAsCanceled: function () {
                this.scheduleTimezone = this.doctor.doctor_metadata.schedule_timezone

                if (this.isDeviceScheduleTimezone == 1) {
                    this.isDeviceScheduleTimezone = 0
                    const params = {
                        schedule_timezone: this.scheduleTimezone,
                        is_device_schedule_timezone: this.isDeviceScheduleTimezone,
                    }
                    axios.put(`${constants.doctor.me.endpoint.base}/schedule_timezone`, params)
                }
            },

            /**
             * デバイスのタイムゾーンに合わせる
             */
            syncDeviceTimezone: function () {
                let shouldSync = true

                if (this.isDeviceScheduleTimezone == 1) {
                    const browserZone = moment.tz.guess()
                    const tz = this.timezones.find((timezone) => timezone.key === browserZone)

                    if (tz && this.scheduleTimezone !== tz.key) {
                        shouldSync = false
                        this.scheduleTimezone = tz.key
                        this.openDoctorTimezoneConfirmModal()
                    }
                }

                if (shouldSync) {
                    const params = {
                        schedule_timezone: this.scheduleTimezone,
                        is_device_schedule_timezone: this.isDeviceScheduleTimezone,
                    }
                    axios.put(`${constants.doctor.me.endpoint.base}/schedule_timezone`, params)
                }
            },

            /**
             * 登録中の基本スケジュールと医師の現在時刻の不整合判定
             * 現在時刻が冬時間だが、スケジュールがサマータイム中に登録されているものならtrue（逆も同様）
             *
             * @returns {boolean}
             */
            isSummerTimeStraddled: function () {
                if (this.basicSchedules.length < 1) {
                    return false
                }

                if (this.helper.isUserSummerTimeNow(this.scheduleTimezone)) {
                    return this.basicSchedules.some((schedule) => !schedule.is_registered_dst)
                }
                return this.basicSchedules.some((schedule) => !!schedule.is_registered_dst)
            },

            /**
             * 基本スケジュール取得
             */
            fetchBasicSchedules: function () {
                return axios.get(`${constants.doctor.schedules.endpoint.base}/raw`)
                    .then(({data}) => {
                        this.basicSchedules = this.convertSchedulesFromJST(data.data)
                    })
            },

            /**
             * 例外スケジュール取得
             */
            fetchExceptionSchedules: function () {
                this.exceptionSchedules = null

                return axios.get(`${constants.doctor.exception_schedules.endpoint.base}/raw`)
                    .then(({data}) => {
                        this.exceptionSchedules = data.data
                    })
            },

            /**
             * 基本スケジュールの時差変換処理
             * @see https://github.com/sewii/Yokumiru/issues/389
             * @param {array} schedules
             */
            convertSchedulesFromJST: function (schedules) {
                if (schedules.length < 1) return []

                const now = moment()
                const doctorOffset = now.clone().tz(schedules[0].timezone).utcOffset()
                const jstOffset = now.clone().tz('Asia/Tokyo').utcOffset()
                const diff = Math.abs(jstOffset - doctorOffset)
                const isGreaterThanJST = doctorOffset > jstOffset

                return schedules.map((schedule) => {
                    if (isGreaterThanJST) {
                        const start = moment(now.format(`YYYY-MM-DD ${schedule.start_time}`))
                        const end = moment(now.format(`YYYY-MM-DD ${schedule.end_time}`))
                        while (start.day() !== schedule.day) {
                            start.add(1, 'days')
                            end.add(1, 'days')
                        }
                        start.add(diff, 'minutes')
                        end.add(diff, 'minutes')

                        schedule.day = start.day()
                        schedule.start_time = start.format('HH:mm:00')
                        schedule.end_time = end.format('HH:mm:00')
                        if (schedule.end_time === '00:00:00') schedule.end_time = '24:00:00'
                    }
                    else {
                        const start = moment(now.format(`YYYY-MM-DD ${schedule.start_time}`))
                        const end = moment(now.format(`YYYY-MM-DD ${schedule.end_time}`))
                        while (start.day() !== schedule.day) {
                            start.add(1, 'days')
                            end.add(1, 'days')
                        }
                        start.subtract(diff, 'minutes')
                        end.subtract(diff, 'minutes')

                        schedule.day = start.day()
                        schedule.start_time = start.format('HH:mm:00')
                        schedule.end_time = end.format('HH:mm:00')
                        if (schedule.end_time === '00:00:00') schedule.end_time = '24:00:00'
                    }
                    return schedule
                })
            },

            /**
             * [FullCalendar]
             * レンダリング完了時
             *
             * @see https://fullcalendar.io/docs/datesRender
             */
            datesRender: function () {
                //「◯日」の「日」を削除
                $('span.fc-day-number').each(function () {
                    if (this.innerText.includes('日')) {
                        this.innerText = this.innerText.slice(0, -1)
                    }
                })
            },

            /**
             * [FullCalendar]
             * イベント描画時
             *
             * @see https://fullcalendar.io/docs/eventRender
             */
            eventRender: function ({event, el, view}) {
                if (view.type === 'dayGridWeek') {
                    el.innerText = event.title
                    this.config.fullCalendar.height = 150
                }
                else {
                    el.innerText = ''
                    this.config.fullCalendar.height = 500
                }
            },

            /**
             * [vue-full-calendar]
             * 登録完了後のカレンダー再描画
             */
            refreshCalendar: async function () {
                await this.fetchMe()
                await this.fetchBasicSchedules()
                await this.fetchExceptionSchedules()

                this.bindFullCalendar()

                this.$refs.fullCalendar.$emit('reload-events')
            },

            /**
             * 相談時間間隔に基づいて
             * チェックボックス列を作成
             */
            prepareCheckbox: function() {
                this.schedule.checkboxes = []
                constants.minutes_intervals.forEach((minutes) => {
                    let columns = []
                    let colIdx = 0

                    this.schedule.columnHours.forEach((hour) => {
                        const timeFrom = moment().hour(hour).minute(0).second(0)
                        const timeTo = moment().hour(hour + 4).minute(0).second(0)

                        let rows = []

                        while (timeTo.diff(timeFrom, 'minute') > 0) {
                            let start = timeFrom.format('HH:mm:ss')
                            let end = timeFrom.add(minutes, 'minutes').format('HH:mm:ss')

                            if (start.substring(0, 2) === '23' && end.substring(0, 2) === '00') {
                                end = '24:00:00'
                            }

                            rows.push({
                                schedule_days: [this.currentDay],
                                day: this.currentDay,
                                start_time: start,
                                end_time: end,
                                column_idx: colIdx,
                            });
                        }
                        columns.push(rows)
                        colIdx++
                    })

                    this.schedule.checkboxes.push(columns)
                } );
            },

            /**
             * チェックボックス列の
             * チェック状態初期化
             */
            initChecked: function () {
                this.schedule.selectedData = []
                this.checkedColumnAllList = []

                this.filteredSchedules.forEach((schedule) => {
                    const checkboxes = this.schedule.checkboxes[this.intervalIdx]

                    checkboxes.forEach((col) => {
                        col.forEach((row) => {
                            if (row.end_time > schedule.start_time && row.start_time < schedule.end_time) {
                                if (!this.schedule.selectedData.includes(row)) {
                                    this.schedule.selectedData.push(row)
                                }
                            }
                        })
                        this.changeColumnState(col[0])
                    })
                })
            },

            /**
             * 列全体のチェック状態を制御
             *
             * @param {object} row
             */
            changeColumnState: function (row) {
                const checkboxes = this.schedule.checkboxes[this.intervalIdx]

                const countColumn = checkboxes[row.column_idx].length
                const countCheckedRow = this.schedule.selectedData.filter((rowTarget) => rowTarget.column_idx === row.column_idx).length
                const idx = this.checkedColumnAllList.findIndex((val) => val === row.column_idx)

                if (countColumn < 1) return

                if (countCheckedRow >= countColumn && idx < 0) {
                    this.checkedColumnAllList.push(row.column_idx)
                }
                else if (countCheckedRow < countColumn && idx > -1) {
                    this.checkedColumnAllList.splice(idx, 1)
                }
            },

            /**
             * 全選択チェックボックスの状態制御
             *
             * @param {number} colIdx 列インデックス
             * @return {boolean}
             */
            existsCheckedIdx: function (colIdx) {
                return this.checkedColumnAllList.includes(colIdx)
            },

            /**
             * チェックボックス列
             * 全選択および全解除
             *
             * @param {array} col
             * @param {number} colIdx
             * @param {object} event
             */
            toggleColumnAll(col, colIdx, event) {
                const $elem = event.target

                // 列のチェックボックス制御
                col.forEach(row => {
                    if ($elem.checked) {
                        if (!this.schedule.selectedData.includes(row)) {
                            this.schedule.selectedData.push(row)
                        }
                    }
                    else {
                        const idx = this.schedule.selectedData.findIndex((data) => data === row)
                        if (idx > -1) {
                            this.schedule.selectedData.splice(idx, 1)
                        }
                    }
                });

                // 全選択チェックボックス制御
                if ($elem.checked) {
                    if (!this.checkedColumnAllList.includes(colIdx)) {
                        this.checkedColumnAllList.push(colIdx)
                    }
                }
                else {
                    const idx = this.checkedColumnAllList.findIndex((val) => val === colIdx)
                    if (idx > -1) {
                        this.checkedColumnAllList.splice(idx, 1)
                    }
                }
            },

            /**
             * [FullCalendar]
             * 日付クリック時
             */
            dateClick: function ({dateStr}) {
                if (this.isSummerTimeStraddled()) {
                    const tz = this.timezones.find((timezone) => timezone.key === this.scheduleTimezone)
                    bus.$emit('open-modal-doctor-timezone-dst-confirm', tz, this.isDeviceScheduleTimezone)
                    return
                }

                this.currentDay = moment(dateStr).day()
                this.prepareCheckbox()
                this.initChecked()
                this.modalShow = true
            },

            /**
             * スケジュール登録ボタン押下時
             */
            handleOk: function(e) {
                e.preventDefault()
                this.postSchedule()
            },

            /**
             * 相談スケジュール間隔毎の予約可能人数を更新
             */
            updatePatientsPerInterval: async function () {
                if (this.inProgress) return
                this.inProgress = true

                const data = { patients_per_interval: this.patientsPerInterval }

                await axios.put(constants.doctor.me.endpoint.base, data)
                    .then(() => {
                        bus.$emit('alert-success', '予約できる件数を更新しました。')
                        this.errors = {}
                    })
                    .catch(() => {
                        bus.$emit('alert-danger', 'システムエラーが発生しました。')
                    })

                this.inProgress = false
            },

            /**
             * 基本スケジュール保存
             */
            postSchedule: async function () {
                if (this.inProgress) return
                this.inProgress = true

                let data, endpoint
                const endpoints = constants.doctor.schedules.endpoint

                if (this.schedule.selectedData.length > 0) {
                    // 保存
                    endpoint = endpoints.base
                    data = {
                        day: this.currentDay,
                        schedules: this.preparePostSchedules(),
                        timezone: this.scheduleTimezone,
                        is_registered_dst: this.helper.isUserSummerTimeNow(this.scheduleTimezone)
                    }
                }
                else {
                    // 削除
                    endpoint = endpoints.delete
                    data = {
                        day: this.currentDay,
                        timezone: this.scheduleTimezone,
                    }
                }

                await axios.post(endpoint, data)
                    .then(() => {
                        bus.$emit('alert-success', '基本スケジュールを保存しました。')
                        bus.$emit('stored-basic-schedule')
                        this.refreshCalendar()
                        this.errors = {}
                        this.modalShow = false
                    })
                    .catch(({response}) => {
                        if (response &&
                            response.hasOwnProperty('data') &&
                            response.data.hasOwnProperty('errors')) {
                            bus.$emit('alert-danger')
                            this.errors = response.data.errors
                        }
                        else {
                            bus.$emit('alert-danger', 'システムエラーが発生しました。')
                        }
                    })

                this.inProgress = false
            },

            /**
             * 基本スケジュール保存リクエスト用のデータ準備
             *
             * @return {array}
             */
            preparePostSchedules: function () {
                const result = []
                let isSplit = false

                // Asia/Tokyo で意図しない is_split が発生しないようにソートしておく
                const sortedData = this.schedule.selectedData.sort((a, b) => {
                    if (a.start_time < b.start_time) return -1
                    if (a.start_time > b.start_time) return 1
                    return 0
                })

                sortedData.forEach((sd) => {
                    const now = moment()

                    const doctorOffset = now.clone().tz(this.scheduleTimezone).utcOffset()
                    const jstOffset = now.clone().tz('Asia/Tokyo').utcOffset()
                    const diff = Math.abs(jstOffset - doctorOffset)
                    const isGreaterThanJST = doctorOffset > jstOffset

                    const start = now.clone().hour(sd.start_time.substring(0, 2)).minute(sd.start_time.substring(3, 5)).second(0).millisecond(0)
                    const end = now.clone().hour(sd.end_time.substring(0, 2)).minute(sd.end_time.substring(3, 5)).second(0).millisecond(0)

                    while (start.day() !== sd.day) {
                        start.add(1, 'day')
                        end.add(1, 'day')
                    }

                    if (isGreaterThanJST) {
                        start.subtract(diff, 'minutes')
                        end.subtract(diff, 'minutes')
                    }
                    else {
                        start.add(diff, 'minutes')
                        end.add(diff, 'minutes')
                    }

                    const data = {...sd}
                    data.day = start.day()
                    data.schedule_days = [start.day()]
                    data.start_time = start.format('HH:mm:00')
                    data.end_time = end.format('HH:mm:00')

                    if (isSplit) {
                        if (data.start_time === '00:00:00') {
                            data.is_split = true
                        }
                        isSplit = false
                    }

                    if (data.start_time > data.end_time) {
                        data.end_time = '24:00:00'
                        result.push(data)
                        isSplit = true
                    }
                    else {
                        result.push(data)
                    }
                })

                return result
            },

            /**
             * [vue-full-calendar]
             * イベント登録
             */
            bindFullCalendar: function () {
                const weeks = []
                const today = moment()
                const weekFirstDate = moment().add(today.day() * -1, 'day')

                for (let i = 0; i < 7; i++) {
                    weeks.push(weekFirstDate.add(Math.ceil(i / 7), 'day').format('YYYY-MM-DD'))
                }

                const events = []
                const schedules = this.basicSchedules.filter((schedule) => !schedule.is_split)
                const splitSchedules = this.basicSchedules.filter((schedule) => schedule.is_split)

                schedules.forEach((schedule) => {
                    const tmpSchedule = {...schedule}
                    const splitSchedule = splitSchedules.find((s) => s.day === tmpSchedule.day && s.start_time === tmpSchedule.end_time)

                    if (splitSchedule) {
                        tmpSchedule.end_time = splitSchedule.end_time
                    }

                    events.push({
                        id: schedule.id,
                        title: `${this.timeForHuman(tmpSchedule.start_time.substring(0, 5))} - ${this.timeForHuman(tmpSchedule.end_time.substring(0, 5))}`,
                        start: weeks[tmpSchedule.day] + ' ' + tmpSchedule.start_time,
                        end: weeks[tmpSchedule.day] + ' ' + tmpSchedule.end_time,
                    })
                })
                this.fullCalendarEvents = events
            },

            /**
             * 午前・午後表記フォーマット
             * @param {string} time
             * @param {boolean} isDisplayAmPm
             * @returns {string}
             */
            timeForHuman: function (time, isDisplayAmPm = true) {
                const hour = Number(time.substring(0, 2))
                const minutes = time.substring(3, 5)

                if (hour < 12) {
                    return isDisplayAmPm ? `午前${time}` : time
                }

                let format = ''
                if (hour === 12) {
                    format = `12:${minutes}`
                } else if (hour === 13) {
                    format = `01:${minutes}`
                } else if (hour === 14) {
                    format = `02:${minutes}`
                } else if (hour === 15) {
                    format = `03:${minutes}`
                } else if (hour === 16) {
                    format = `04:${minutes}`
                } else if (hour === 17) {
                    format = `05:${minutes}`
                } else if (hour === 18) {
                    format = `06:${minutes}`
                } else if (hour === 19) {
                    format = `07:${minutes}`
                } else if (hour === 20) {
                    format = `08:${minutes}`
                } else if (hour === 21) {
                    format = `09:${minutes}`
                } else if (hour === 22) {
                    format = `10:${minutes}`
                } else if (hour === 23) {
                    format = `11:${minutes}`
                } else if (hour === 24) {
                    format = `00:${minutes}`
                }

                if (isDisplayAmPm) {
                    return hour === 24 ? `午前${format}` : `午後${format}`
                }
                return format
            },

            openDoctorCalendarExportModal: function () {
                bus.$emit('open-modal-doctor-calendar-export')
            },

            /**
             * スケジュールクリア確認モーダルの表示
             */
            openDoctorTimezoneConfirmModal: function () {
                const tz = this.timezones.find((timezone) => timezone.key === this.scheduleTimezone)
                bus.$emit('open-modal-doctor-timezone-confirm', tz, this.isDeviceScheduleTimezone)
            },

            /**
             * Googleアカウント連携モーダルの表示
             */
            openDoctorGoogleCalendarLinkageModal: function () {
                bus.$emit('open-modal-doctor-google-calendar-linkage')
            },

            /**
             * Googleカレンダー連携モーダルの表示
             */
            openDoctorGoogleAccountLinkModal: function () {
                bus.$emit('open-modal-doctor-google-account-link')
            },

            /**
             * Googleアカウント連携解除モーダルの表示
             */
            openDoctorGoogleAccountLinkCancelModal: function () {
                bus.$emit('open-modal-doctor-google-account-link-cancel')
            },

            /**
             * Googleカレンダー連携変更時
             * @return {Promise<void>}
             */
            changeLinkCalendar: async function () {
                if (this.inProgress) return
                this.inProgress = true

                await this.fetchMe()
                await this.fetchExceptionSchedules()
                this.bindFullCalendar()

                this.inProgress = false
            },
        },
    }
</script>

<style scoped lang="scss">
.cautionTimezone {
    margin-top: 4px;
    font-size: 13px;

    svg {
        margin-right: 4px;
        fill: #f79e08;
    }
}

.calendar-btn{
    width: 100%;
    background: #d12e4f;
    margin: auto 0 16px 0;

    &:hover {
        opacity: 0.7;
    }
}

.link-calendar-btn{
    width: 100%;
    margin: auto 0 16px 0;

    &:hover {
        opacity: 0.7;
    }
}
</style>

<style lang="scss">
.vc-basic-schedule-table {
    .fc-dayGridWeek-view {
        .fc-event {
            padding: 4px;
            font-size: .7rem;
            font-weight: bold;
            background: #FB6F78;
            border: none;
        }

        .fc-title {
            display: none;
        }
    }

    .fc-timeGridWeek-view {
        .fc-event {
            background: #FD5E63;
            border: none;

            &::after {
                content: '診療';
                position: absolute;
                left: 50%;
                top: 50%;
                color: #fff;
                font-size: .875rem;
                transform: translate(-50%, -50%);
            }

            .fc-content {
                .fc-time {
                    span {
                        display: none;
                    }
                }

                .fc-title {
                    display: none;
                }
            }
        }
    }

    .schedule-calendar-consul {
        .fc-timeGridWeek-view {
            .fc-event {
                &::after {
                    content: '相談';
                }
            }
        }
    }
}
</style>
